import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';

const SPONSORS = [
  { name: 'Crow Safety Gear', src: '/sponsor-crow-safety-gear.png', url: 'https://www.crowsafetygear.com',  dark: false },
  { name: "Tub O' Towels",    src: '/sponsor-tub-o-towels.jpg',     url: 'https://www.tubotowels.com',      dark: false },
  { name: 'Fervogear',        src: '/sponsor-fervogear.png',        url: 'https://www.fervogear.com',       dark: true  },
  { name: 'Church of Speed',  src: '/sponsor-church-of-speed.jpg',  url: 'https://church-of-speed.com',    dark: false },
  { name: 'DART',             src: '/sponsor-dart.png',             url: 'https://www.dartheads.com',       dark: false },
];

const NAV_LINK: React.CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  textDecoration: 'none',
  paddingBottom: '2px',
  transition: 'color 0.15s',
};

export const Navigation: React.FC = () => {
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user, logout } = useAuth();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <nav>
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{ background: '#0A0A0A', borderBottom: '1px solid rgba(255,255,255,0.06)', height: '56px' }}
      >
        <div className="flex items-center h-full px-5 max-w-screen-2xl mx-auto gap-4">

          {/* Wordmark */}
          <Link to="/" className="shrink-0 hover:opacity-75 transition-opacity" style={{ lineHeight: 1 }}>
            <span style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '26px',
              letterSpacing: '0.08em',
              color: '#EEEAD5',
              lineHeight: 1,
            }}>
              RACECARLY
            </span>
          </Link>

          <div style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              style={{
                ...NAV_LINK,
                color: isActive('/') ? '#EEEAD5' : '#888874',
                borderBottom: isActive('/') ? '2px solid #D93025' : '2px solid transparent',
              }}
              onMouseEnter={e => { if (!isActive('/')) (e.currentTarget as HTMLElement).style.color = '#EEEAD5'; }}
              onMouseLeave={e => { if (!isActive('/')) (e.currentTarget as HTMLElement).style.color = '#888874'; }}
            >
              Home
            </Link>

            {/* Social icons */}
            <a
              href="https://www.instagram.com/racecar.ly/"
              target="_blank"
              rel="noopener noreferrer"
              title="Instagram"
              className="transition-colors"
              style={{ color: '#888874', display: 'flex', alignItems: 'center', padding: '4px' }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#EEEAD5')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#888874')}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@racecarly"
              target="_blank"
              rel="noopener noreferrer"
              title="YouTube"
              className="transition-colors"
              style={{ color: '#888874', display: 'flex', alignItems: 'center', padding: '4px' }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#EEEAD5')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#888874')}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>

          {/* Sponsors + Auth — right side */}
          <div className="hidden md:flex items-center gap-3 ml-auto">
            <span style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: '#AEAA94',
              textTransform: 'uppercase',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}>
              2026 Sponsors
            </span>

            <div className="flex items-center gap-1.5">
              {SPONSORS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 hover:opacity-70 transition-opacity"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: s.dark ? '#161616' : 'rgba(255,255,255,0.90)',
                    padding: '3px 8px',
                    height: '30px',
                    borderRadius: '3px',
                    border: s.dark ? '1px solid rgba(255,255,255,0.08)' : 'none',
                  }}
                >
                  <img src={s.src} alt={s.name} style={{ height: '20px', width: 'auto', objectFit: 'contain', display: 'block', maxWidth: '76px' }} />
                </a>
              ))}
            </div>

            <div style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

            {/* Auth */}
            {isAuthenticated && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#888874',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.08)',
                    padding: '5px 12px',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'border-color 0.15s, color 0.15s',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.14)';
                    (e.currentTarget as HTMLElement).style.color = '#EEEAD5';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
                    (e.currentTarget as HTMLElement).style.color = '#888874';
                  }}
                >
                  <UserCircleIcon className="w-4 h-4" />
                  <ChevronDownIcon className={`w-3 h-3 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isUserMenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 'calc(100% + 4px)',
                      width: '200px',
                      background: '#131313',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '4px',
                      padding: '8px',
                      zIndex: 100,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                    }}
                  >
                    <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '6px' }}>
                      <p style={{ fontSize: '10px', color: '#888874', fontFamily: "'DM Sans'", marginBottom: '2px' }}>Signed in as</p>
                      <p style={{ fontSize: '13px', color: '#EEEAD5', fontFamily: "'DM Sans'", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</p>
                    </div>

                    {[
                      { to: '/profile', icon: <UserCircleIcon className="w-4 h-4" />, label: 'Profile', show: true },
                      { to: '/admin', icon: <ChartBarIcon className="w-4 h-4" />, label: 'Admin', show: !!user?.isAdmin },
                      { to: '/settings', icon: <Cog6ToothIcon className="w-4 h-4" />, label: 'Settings', show: true },
                    ].filter(item => item.show).map(item => (
                      <Link
                        key={item.to}
                        to={item.to}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          borderRadius: '3px',
                          color: '#888874',
                          textDecoration: 'none',
                          fontSize: '13px',
                          fontFamily: "'DM Sans'",
                          transition: 'color 0.1s, background 0.1s',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.color = '#EEEAD5';
                          (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.color = '#888874';
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                        }}
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    ))}

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '6px', paddingTop: '6px' }}>
                      <button
                        onClick={() => { setIsUserMenuOpen(false); logout(); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '3px',
                          color: '#D93025',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontFamily: "'DM Sans'",
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = 'rgba(217,48,37,0.08)')}
                        onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden ml-auto transition-colors"
            style={{
              color: '#888874',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
            }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#EEEAD5')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = '#888874')}
          >
            {isMobileMenuOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0"
            style={{ background: 'rgba(0,0,0,0.75)' }}
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '56px',
              left: 0,
              right: 0,
              background: '#0A0A0A',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              padding: '16px 20px',
              zIndex: 50,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Link
                to="/"
                style={{
                  ...NAV_LINK,
                  color: isActive('/') ? '#EEEAD5' : '#888874',
                  padding: '10px 0',
                  borderBottom: 'none',
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '12px', paddingTop: '16px' }}>
                <p style={{ fontFamily: "'DM Sans'", fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', color: '#AEAA94', textTransform: 'uppercase', marginBottom: '12px' }}>
                  2026 Sponsors
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {SPONSORS.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: s.dark ? '#161616' : 'rgba(255,255,255,0.90)',
                        padding: '3px 8px',
                        height: '30px',
                        borderRadius: '3px',
                        border: s.dark ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      }}
                    >
                      <img src={s.src} alt={s.name} style={{ height: '20px', width: 'auto', objectFit: 'contain', display: 'block', maxWidth: '76px' }} />
                    </a>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '12px' }}>
                <p style={{ fontFamily: "'DM Sans'", fontSize: '9px', fontWeight: 700, letterSpacing: '0.22em', color: '#888874', textTransform: 'uppercase', marginBottom: '12px' }}>Follow</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a
                    href="https://www.instagram.com/racecar.ly/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#888874', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: '10px', borderRadius: '3px', display: 'flex' }}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a
                    href="https://www.youtube.com/@racecarly"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#888874', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: '10px', borderRadius: '3px', display: 'flex' }}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
